import Badge from "@/components/common/Badge";

interface TutorialProgressProps {
  tutorialProgress: {
    progress: number;
    isCompleted: boolean;
    isInProgress: boolean;
    lastWatchedAt?: string;
  } | null;
  t: (key: string) => string;
  partNumber: number;
}

const TutorialProgress = ({ tutorialProgress, t, partNumber }: TutorialProgressProps) => {
  if (!tutorialProgress) {
    return (
      <div className="mb-2 sm:mb-3 space-y-1.5 sm:space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <Badge variant="primary" className="text-[10px] sm:text-xs">
              <span className="mr-0.5 sm:mr-1">📚</span>
              {t("courses.notStarted")}
            </Badge>
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
              {t("courses.part")} {partNumber}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 sm:mb-3 space-y-1.5 sm:space-y-2">
      {/* Progress Badge and Percentage */}
      <div className="flex items-center justify-between gap-2">
        {/* Progress Badge */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
          {tutorialProgress.isCompleted ? (
            <Badge variant="success" className="text-[10px] sm:text-xs truncate">
              <span className="mr-0.5 sm:mr-1">✅</span>
              {t("courses.completed")}
            </Badge>
          ) : tutorialProgress.isInProgress ? (
            <Badge variant="warning" className="text-[10px] sm:text-xs truncate">
              <span className="mr-0.5 sm:mr-1">🔄</span>
              {t("courses.inProgress")}
            </Badge>
          ) : (
            <Badge variant="primary" className="text-[10px] sm:text-xs truncate">
              <span className="mr-0.5 sm:mr-1">📚</span>
              {t("courses.notStarted")}
            </Badge>
          )}
          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {t("courses.part")} {partNumber}
          </span>
        </div>

        {/* Progress Percentage */}
        {tutorialProgress.isInProgress && (
          <span className="text-[10px] sm:text-xs font-medium text-primary-600 dark:text-primary-400 whitespace-nowrap">
            {tutorialProgress.progress}%
          </span>
        )}
      </div>

      {/* Progress Bar for In-Progress Tutorials */}
      {tutorialProgress.isInProgress && (
        <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
          <div
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${tutorialProgress.progress}%`,
            }}
          ></div>
        </div>
      )}

      {/* Last Watched Info for In-Progress Tutorials */}
      {tutorialProgress.isInProgress && tutorialProgress.lastWatchedAt && (
        <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
          {t("courses.lastWatched")}:{" "}
          {new Date(tutorialProgress.lastWatchedAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default TutorialProgress;
